import React, { useState, useRef, useEffect } from "react";
import {
    AiOutlineClockCircle,
    AiOutlineMessage,
    AiOutlineMore,
    AiOutlineWarning,
    AiOutlineEdit,
    AiOutlineDelete,
} from "react-icons/ai";
import { Comment } from "./types";
import { formatTime, isDrawingPlaceholder } from "./utils";
import { AddComment } from "./AddComment";
import Modal from "../Modal";
import { Dropdown } from "../Dropdown";
import axios from "axios";
import CommentTimestamp from "./CommentTimestamp";
import { MdDraw, MdPlayArrow } from "react-icons/md";

const AVATAR_COLORS = [
    "bg-violet-500",
    "bg-sky-500",
    "bg-emerald-500",
    "bg-amber-500",
    "bg-rose-500",
    "bg-teal-500",
    "bg-indigo-500",
    "bg-pink-500",
    "bg-orange-500",
    "bg-cyan-600",
];

const avatarColorFor = (key: string): string => {
    let hash = 0;
    for (let i = 0; i < key.length; i++) {
        hash = (hash * 31 + key.charCodeAt(i)) | 0;
    }
    return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
};

interface CommentItemProps {
    comment: Comment;
    indexId: string;
    onSeek: (time: number) => void;
    onCommentSelect: (comment: Comment) => void;
    onCommentUpdate: () => void;
    videoId: string;
    isPlaying: boolean;
    setIsPlaying: (playing: boolean) => void;
    currentTime: number;
    currentUserEmail: string;
    fetchReplies: (commentId: string) => Promise<Comment | null>;
    depth: number;
    isLoadingReplies?: boolean;
    readOnly: boolean;
    shareToken?: string;
    selectedCommentId: string | null;
    setSelectedCommentId: (id: string) => void;
}

export const CommentItem: React.FC<CommentItemProps> = ({
    comment,
    indexId,
    onSeek,
    onCommentSelect,
    onCommentUpdate,
    videoId,
    isPlaying,
    setIsPlaying,
    currentTime,
    currentUserEmail,
    fetchReplies,
    depth,
    isLoadingReplies,
    readOnly,
    shareToken,
    selectedCommentId,
    setSelectedCommentId,
}) => {
    const [showReplies, setShowReplies] = useState(false);
    const [isReplying, setIsReplying] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [loadedReplies, setLoadedReplies] = useState<Comment[]>([]);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [isDeletingComment, setIsDeletingComment] = useState(false);
    const maxDepth = 3;

    const commentRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (selectedCommentId === comment.id && commentRef.current) {
            commentRef.current.scrollIntoView({
                behavior: "smooth",
                block: "center",
            });
        }
    }, [selectedCommentId, comment.id]);

    const isSelected = selectedCommentId === comment.id;
    const annotationEnd = Math.max(comment.start_time, comment.end_time);
    const annotationTolerance = annotationEnd === comment.start_time ? 0.25 : 0;
    const isViewingDrawing = Boolean(
        comment.annotation &&
        isSelected &&
        !isPlaying &&
        currentTime >= comment.start_time - annotationTolerance &&
        currentTime <= annotationEnd + annotationTolerance
    );

    const handleSelectComment = () => {
        onCommentSelect(comment);
    };

    const showDrawing = () => {
        setSelectedCommentId(comment.id);
        onSeek(comment.start_time);
        setIsPlaying(false);
    };

    const handleViewDrawing = () => {
        if (isViewingDrawing) {
            setIsPlaying(true);
            return;
        }

        showDrawing();
    };

    const getHeaders = () => {
        if (shareToken) {
            return { "X-Share-Token": shareToken };
        }
        const token = localStorage.getItem("token");
        return { Authorization: `Bearer ${token}` };
    };

    const handleDelete = async () => {
        setIsDeletingComment(true);
        try {
            await axios.delete(
                `${process.env.REACT_APP_API}/api/quick-review/comments/${videoId}/${comment.id}`,
                { headers: getHeaders() }
            );
            onCommentUpdate();
            setShowDeleteModal(false);
        } catch (error) {
            console.error("Error deleting comment:", error);
            alert("Failed to delete comment. Please try again.");
        } finally {
            setIsDeletingComment(false);
        }
    };

    const handleToggleReplies = async () => {
        if (!showReplies && comment.reply_count > 0) {
            const repliesData = await fetchReplies(comment.id);
            if (repliesData) {
                setLoadedReplies(repliesData.replies || []);
            }
        }
        setShowReplies(!showReplies);
    };

    const handleCommentSuccess = async () => {
        setIsReplying(false);
        setIsEditing(false);

        if (showReplies) {
            const repliesData = await fetchReplies(comment.id);
            if (repliesData) {
                setLoadedReplies(repliesData.replies || []);
            }
        }

        onCommentUpdate();
    };

    const dropdownItems = [
        {
            label: "Edit",
            icon: <AiOutlineEdit className="text-lg" />,
            onClick: () => {
                setIsEditing(true);
                // Show the drawing while its text is edited.
                if (comment.annotation) showDrawing();
            },
            className: "text-gray-700",
        },
        {
            label: "Delete",
            icon: <AiOutlineDelete className="text-lg" />,
            onClick: () => setShowDeleteModal(true),
            className: "text-error",
        },
    ];

    return (
        <div
            ref={commentRef}
            data-comment-id={comment.id}
            className={`
                p-3 rounded-lg
                ${depth > 0 ? "ml-4" : ""} 
                ${isSelected ? "ring-1 ring-gray-400 bg-gray-50" : "bg-white hover:bg-gray-50"}
                transition-all duration-200
            `}
            onClick={event => {
                event.stopPropagation();
                handleSelectComment();
            }}
        >
            <div className="flex items-center justify-between gap-2 text-sm">
                <div className="flex items-center gap-2">
                    <div
                        className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-medium text-white ${avatarColorFor(
                            comment.user_email || comment.first_name || "?"
                        )}`}
                    >
                        {(comment.first_name || comment.last_name || comment.user_email || "?")
                            .charAt(0)
                            .toUpperCase()}
                    </div>
                    <span className="font-medium">
                        {comment.first_name} {comment.last_name}
                    </span>
                    <span className="opacity-50">•</span>
                    <span className="opacity-50">
                        <CommentTimestamp date={comment.created_at} />
                        {comment.is_edited && " (edited)"}
                    </span>
                </div>
                {comment.user_email === currentUserEmail && (
                    // Using the menu shouldn't select the comment or toggle playback.
                    <div onClick={event => event.stopPropagation()}>
                        <Dropdown
                            trigger={<AiOutlineMore className="text-lg" />}
                            items={dropdownItems}
                            align="right"
                            triggerClassName="btn btn-ghost btn-xs rounded-full"
                            menuClassName="w-44"
                            itemClassName="text-sm"
                        />
                    </div>
                )}
            </div>

            {isEditing ? (
                // Clicking into the edit box shouldn't toggle playback away from the drawing.
                <div onClick={event => event.stopPropagation()}>
                    <AddComment
                        videoId={videoId}
                        indexId={indexId}
                        currentTime={currentTime}
                        isPlaying={isPlaying}
                        setIsPlaying={setIsPlaying}
                        onCommentAdded={handleCommentSuccess}
                        initialContent={isDrawingPlaceholder(comment) ? "" : comment.content}
                        commentId={comment.id}
                        isEditing={true}
                        onCancel={() => setIsEditing(false)}
                        shareToken={shareToken}
                        currentUserEmail={currentUserEmail}
                        annotation={comment.annotation}
                        annotationTime={comment.start_time}
                    />
                </div>
            ) : (
                <p className="mt-2 mb-3 break-words leading-relaxed">
                    <button
                        type="button"
                        className="mr-2 inline-flex items-center gap-1 rounded bg-gray-100 px-1.5 py-0.5 align-baseline text-xs font-medium tabular-nums text-gray-600 transition-colors hover:bg-gray-200 hover:text-gray-900 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-purple-500"
                        title={
                            isSelected && !isPlaying
                                ? "Resume video"
                                : `Jump to ${formatTime(comment.start_time)} and pause`
                        }
                        onClick={e => {
                            e.stopPropagation();
                            handleSelectComment();
                        }}
                    >
                        <AiOutlineClockCircle aria-hidden="true" />
                        <span className="whitespace-nowrap">
                            {formatTime(comment.start_time)}
                            {comment.end_time !== comment.start_time && (
                                <> – {formatTime(comment.end_time)}</>
                            )}
                        </span>
                    </button>
                    {isDrawingPlaceholder(comment) ? (
                        <span className="italic text-gray-500">Drawing</span>
                    ) : (
                        <span className="whitespace-pre-wrap">{comment.content}</span>
                    )}
                </p>
            )}

            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs font-medium text-gray-500">
                {depth < maxDepth && !readOnly && (
                    <button
                        type="button"
                        className="inline-flex min-h-7 items-center gap-1.5 rounded hover:text-gray-900 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-purple-500"
                        onClick={e => {
                            e.stopPropagation();
                            setIsReplying(!isReplying);
                        }}
                    >
                        <AiOutlineMessage size={14} aria-hidden="true" /> Reply
                    </button>
                )}
                {!isEditing && isSelected && !isPlaying && !isViewingDrawing && (
                    <button
                        type="button"
                        className="inline-flex min-h-7 items-center gap-1.5 rounded text-purple-700 hover:text-purple-900 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-purple-500"
                        title="Resume video"
                        onClick={e => {
                            e.stopPropagation();
                            setIsPlaying(true);
                        }}
                    >
                        <MdPlayArrow size={15} aria-hidden="true" />
                        Resume video
                    </button>
                )}
                {!isEditing && comment.annotation && (
                    <button
                        type="button"
                        className={`inline-flex min-h-7 items-center gap-1.5 rounded focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-purple-500 ${
                            isViewingDrawing ? "text-purple-700" : "hover:text-gray-900"
                        }`}
                        title={
                            isViewingDrawing
                                ? "Resume video"
                                : `Jump to ${formatTime(comment.start_time)} and pause to view drawing`
                        }
                        aria-label={
                            isViewingDrawing
                                ? "Resume video"
                                : `View drawing at ${formatTime(comment.start_time)}. Pauses video.`
                        }
                        onClick={event => {
                            event.stopPropagation();
                            handleViewDrawing();
                        }}
                    >
                        {isViewingDrawing ? (
                            <MdPlayArrow size={15} aria-hidden="true" />
                        ) : (
                            <MdDraw size={14} aria-hidden="true" />
                        )}
                        {isViewingDrawing ? "Resume video" : "View drawing"}
                    </button>
                )}
                {comment.reply_count > 0 && (
                    <button
                        type="button"
                        className="inline-flex min-h-7 items-center rounded hover:text-gray-900 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-purple-500 disabled:opacity-50"
                        onClick={e => {
                            e.stopPropagation();
                            handleToggleReplies();
                        }}
                        disabled={isLoadingReplies}
                    >
                        {showReplies ? "Hide" : "Show"} {comment.reply_count} replies
                    </button>
                )}
            </div>

            {isReplying && (
                <div className="mt-2">
                    <AddComment
                        videoId={videoId}
                        indexId={indexId}
                        currentTime={currentTime}
                        isPlaying={isPlaying}
                        setIsPlaying={setIsPlaying}
                        onCommentAdded={handleCommentSuccess}
                        parentComment={comment}
                        onCancel={() => setIsReplying(false)}
                        shareToken={shareToken}
                        currentUserEmail={currentUserEmail}
                    />
                </div>
            )}

            {showReplies && loadedReplies.length > 0 && (
                <div className="space-y-2 mt-2">
                    {isLoadingReplies ? (
                        <div className="flex justify-center py-2">
                            <div className="loading loading-spinner loading-sm text-primary"></div>
                        </div>
                    ) : (
                        loadedReplies.map(reply => (
                            <CommentItem
                                key={reply.id}
                                comment={reply}
                                indexId={indexId}
                                onSeek={onSeek}
                                onCommentSelect={onCommentSelect}
                                onCommentUpdate={handleCommentSuccess}
                                videoId={videoId}
                                isPlaying={isPlaying}
                                setIsPlaying={setIsPlaying}
                                currentTime={currentTime}
                                currentUserEmail={currentUserEmail}
                                fetchReplies={fetchReplies}
                                depth={depth + 1}
                                readOnly={readOnly}
                                shareToken={shareToken}
                                selectedCommentId={selectedCommentId}
                                setSelectedCommentId={setSelectedCommentId}
                            />
                        ))
                    )}
                </div>
            )}

            <Modal
                isOpen={showDeleteModal}
                onClose={() => !isDeletingComment && setShowDeleteModal(false)}
                title="Confirm Delete"
            >
                <div className="flex flex-col items-center gap-4 p-6">
                    <AiOutlineWarning className="text-6xl text-yellow-500" />
                    <h2 className="text-xl font-semibold text-center">
                        Are you sure you want to delete this comment?
                    </h2>
                    <p className="text-gray-600 text-center">This action cannot be undone.</p>
                    <div className="flex justify-center gap-4 mt-4">
                        <button
                            className="btn btn-ghost"
                            onClick={() => setShowDeleteModal(false)}
                            disabled={isDeletingComment}
                        >
                            Cancel
                        </button>
                        <button
                            className="btn bg-red-500 hover:bg-red-600 text-white"
                            onClick={handleDelete}
                            disabled={isDeletingComment}
                        >
                            {isDeletingComment ? (
                                <>
                                    <span className="loading loading-spinner loading-sm"></span>
                                    Deleting...
                                </>
                            ) : (
                                "Delete"
                            )}
                        </button>
                    </div>
                </div>
            </Modal>
        </div>
    );
};
